import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { notificationsAPI } from 'api/notifications'

interface Notification {
 id: number
 user_id: number
 branch_id?: string | null
 branch_name?: string | null
 type: string
 title: string
 message: string
 data?: any
 action_url?: string | null
 read: boolean
 read_at?: string
 created_at: string
 updated_at: string
}

interface NotificationSummary {
 total: number
 unread: number
 alerts: number
}

interface NotificationsState {
 data: Notification[]
 unreadCount: number
 summary: NotificationSummary
 loading: boolean
 error: string | null
 pagination: {
 total: number
 per_page: number
 current_page: number
 last_page: number
 } | null
}

const initialSummary: NotificationSummary = { total: 0, unread: 0, alerts: 0 }

const initialState: NotificationsState = {
 data: [],
 unreadCount: 0,
 summary: initialSummary,
 loading: false,
 error: null,
 pagination: null,
}

export const fetchNotifications = createAsyncThunk(
 'notifications/fetchNotifications',
 async (params: any = {}, { rejectWithValue }) => {
 try {
 const response = await notificationsAPI.getAll(params)
 return response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.notifications.fetchFailed'))
 }
 }
)

export const fetchSummary = createAsyncThunk(
 'notifications/fetchSummary',
 async (_, { rejectWithValue }) => {
 try {
 const response = await notificationsAPI.getSummary()
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.notifications.summaryFailed'))
 }
 }
)

export const fetchUnreadCount = createAsyncThunk(
 'notifications/fetchUnreadCount',
 async (_, { rejectWithValue }) => {
 try {
 const response = await notificationsAPI.getUnreadCount()
 return response.data?.count || 0
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.notifications.unreadCountFailed'))
 }
 }
)

export const markAsRead = createAsyncThunk(
 'notifications/markAsRead',
 async (id: number, { rejectWithValue }) => {
 try {
 const response = await notificationsAPI.markAsRead(id)
 return { id, summary: response.summary }
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.notifications.markReadFailed'))
 }
 }
)

export const markAllAsRead = createAsyncThunk(
 'notifications/markAllAsRead',
 async (_, { rejectWithValue }) => {
 try {
 const response = await notificationsAPI.markAllAsRead()
 return response.summary
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.notifications.markAllReadFailed'))
 }
 }
)

export const deleteNotification = createAsyncThunk(
 'notifications/deleteNotification',
 async (id: number, { rejectWithValue }) => {
 try {
 const response = await notificationsAPI.delete(id)
 return { id, summary: response.summary }
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.notifications.deleteOneFailed'))
 }
 }
)

export const clearAllNotifications = createAsyncThunk(
 'notifications/clearAllNotifications',
 async (_, { rejectWithValue }) => {
 try {
 const response = await notificationsAPI.clearAll()
 return response.summary
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.notifications.clearFailed'))
 }
 }
)

const notificationsSlice = createSlice({
 name: 'notifications',
 initialState,
 reducers: {
 clearNotifications: (state) => {
 state.data = []
 state.unreadCount = 0
 state.summary = initialSummary
 state.error = null
 state.pagination = null
 },
 },
 extraReducers: (builder) => {
 builder
 .addCase(fetchNotifications.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchNotifications.fulfilled, (state, action) => {
 state.loading = false
 const payload = action.payload as any
 const data = payload?.data || payload || []
 state.data = Array.isArray(data) ? data : []
 state.pagination = payload?.pagination || null
 if (payload?.summary) {
 state.summary = payload.summary
 state.unreadCount = payload.summary.unread
 }
 })
 .addCase(fetchNotifications.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(fetchSummary.fulfilled, (state, action) => {
 const summary = action.payload as NotificationSummary
 if (summary) {
 state.summary = summary
 state.unreadCount = summary.unread
 }
 })
 .addCase(fetchUnreadCount.fulfilled, (state, action) => {
 state.unreadCount = action.payload as number
 state.summary.unread = action.payload as number
 })
 .addCase(markAsRead.fulfilled, (state, action) => {
 const { id, summary } = action.payload as { id: number; summary?: NotificationSummary }
 const notification = state.data.find((n) => n.id === id)
 if (notification) {
 notification.read = true
 notification.read_at = new Date().toISOString()
 }
 if (summary) {
 state.summary = summary
 state.unreadCount = summary.unread
 } else {
 state.unreadCount = Math.max(0, state.unreadCount - 1)
 state.summary.unread = state.unreadCount
 }
 })
 .addCase(markAllAsRead.fulfilled, (state, action) => {
 state.data.forEach((notification) => {
 notification.read = true
 notification.read_at = new Date().toISOString()
 })
 const summary = action.payload as NotificationSummary | undefined
 if (summary) {
 state.summary = summary
 state.unreadCount = summary.unread
 } else {
 state.unreadCount = 0
 state.summary.unread = 0
 }
 })
 .addCase(deleteNotification.fulfilled, (state, action) => {
 const { id, summary } = action.payload as { id: number; summary?: NotificationSummary }
 state.data = state.data.filter((n) => n.id !== id)
 if (summary) {
 state.summary = summary
 state.unreadCount = summary.unread
 } else {
 state.unreadCount = Math.max(0, state.unreadCount - 1)
 state.summary.unread = state.unreadCount
 }
 })
 .addCase(clearAllNotifications.fulfilled, (state, action) => {
 state.data = []
 const summary = action.payload as NotificationSummary | undefined
 if (summary) {
 state.summary = summary
 state.unreadCount = summary.unread
 } else {
 state.unreadCount = 0
 state.summary = initialSummary
 }
 state.pagination = state.pagination
 ? {
 ...state.pagination,
 total: 0,
 current_page: 1,
 last_page: 1,
 }
 : null
 })
 },
})

export const { clearNotifications } = notificationsSlice.actions
export default notificationsSlice.reducer
